import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import typeOfRemuneration from '../../../../../../../core/jsons/typeOfRemuneration.json';
import { ProvinceEntity } from 'src/app/core/services/location/domain/entities/province.entity';
import { LocationEntity } from 'src/app/core/services/location/domain/entities/location.entity';
import { GetProvinceUseCase } from 'src/app/core/services/location/domain/usecases/get-province.usecase';
import { GetLocationsUseCase } from 'src/app/core/services/location/domain/usecases/get-locations.usecase';
import { ClaimEntity } from 'src/app/features/claims/domain/entities/claim.entity';
import { GetFileUseCase } from 'src/app/features/claims/domain/usecases/get-file.usecase';
import { GetFileModel } from 'src/app/features/claims/data/models/get-file.model';
import { Failure } from 'src/app/core/utils/failure';
import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { CalendarTranslationService } from 'src/app/core/services/translate-calendar.service';
import { UserData } from 'src/app/features/user/domain/entities/user-data.entity';
import { Toast } from 'primeng/toast';
import { Departament } from 'src/app/features/user/presentation/components/New-User-SuperAdmin/newUser.component';
import { GetDepartmentsUseCase } from 'src/app/core/services/location/domain/usecases/get-departments.usecase';
import { GetDepartmentsByConciliadorUseCase } from 'src/app/core/services/location/domain/usecases/get-departments-by-conciliador.usecase';
import { GetDepartmentsByIdUseCase } from 'src/app/core/services/location/domain/usecases/get-department-by-id.usescase';
import { dateControl } from 'src/app/core/utils/date-validator';

interface TypeRemeneration {
  id: number;
  type: string;
}

interface Localities {
  id: number;
  name: string;
}

interface Base64 {
  name: string;
  base64: string;
}

/** CUIL/CUIT: valida 11 dígitos; el valor puede venir con máscara (guiones). */
function cuilElevenDigitsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (v == null || v === '') {
      return null;
    }
    const digits = String(v).replace(/\D/g, '');
    return digits.length === 11 ? null : { cuilInvalid: true };
  };
}

@Component({
  selector: 'app-claimant-details-form',
  templateUrl: './claimant-details-form.component.html',
  styleUrls: ['./claimant-details-form.component.css'],
  providers: [ConfirmationService, MessageService, Toast],
})
export class ClaimantDetailsFormComponent implements OnInit, OnChanges {
  @Output() claimantDetailsForm: EventEmitter<FormGroup> = new EventEmitter();
  @Output() stepIndex: EventEmitter<number> = new EventEmitter();
  @Output() activeModal: EventEmitter<boolean> = new EventEmitter();

  @Input() claim?: ClaimEntity;
  @Input() claimId?: string | null;
  @Input() userData?: UserData;

  typeRemunerations: TypeRemeneration[] = [...typeOfRemuneration];
  uploadedFiles: any[] = [];
  visibleCancel: boolean = false;
  acceptsElectronicDomicile: boolean = false;
  showConditionalReason: boolean = false;
  showConditionalCause: boolean = false;
  disableFileImageUpload: boolean = false;
  disableFilePdfUpload: boolean = false;
  maxDate: Date | undefined;
  maxDateIngreso: Date | undefined;
  minDate: Date | undefined;

  maxDateForDateAdmission: Date | null = null;
  minDateForEgressDate: Date | null = null;
  updatingDateAdmission = false;
  updatingEgressDate = false;

  isDragging: boolean = false;
  imageFiles: any[] = [];
  pdfFiles: any[] = [];
  departments!: Departament[];
  progress: number = 0;

  provinces: ProvinceEntity[] = [];
  localities: Localities[] = [];
  selectedProvince?: ProvinceEntity;
  selectedDepartment?: Departament;
  public isEditMode: boolean = false;

  typeOfJob: any[] = [
    { name: 'Registrado', key: 'register' },
    { name: 'Parcialmente registrado', key: 'partRegister' },
  ];

  currentEmploymentsStatus: any[] = [
    { id: 1, name: 'Vigente' },
    { id: 2, name: 'Disuelta' },
  ];
  reasonsForDisengagement: any[] = [
    { id: 1, name: 'Renuncia' },
    { id: 2, name: 'Despido indirecto' },
    { id: 3, name: 'Mutuo acuerdo' },
    { id: 4, name: 'Despido' },
  ];
  causesDisengagement: any[] = [
    { id: 1, name: 'Invocación de causa' },
    { id: 2, name: 'Sin causa' },
    { id: 3, name: 'Articulo 247' },
  ];

  typeOfRepresentation = [
    { name: 'Actuo en nombre propio', key: 'personalName' },
    { name: 'En representación del trabajador', key: 'workerName' },
  ];

  isRepresentationWorker: boolean = false;

  isRepresentative: boolean = false;

  formClaimetDetails: FormGroup = this.formBuilder.group({
    claimetLastname: [
      '',
      [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/),
      ],
    ],
    claimetName: [
      '',
      [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/),
      ],
    ],
    claimetCuil: ['', [Validators.required]],
    claimetEmail: [
      '',
      [Validators.required, Validators.email, Validators.maxLength(100)],
    ],
    claimetPhone: [
      '',
      [
        Validators.minLength(7),
        Validators.maxLength(15),
        Validators.pattern(/^[0-9]+$/),
      ],
    ],
    claimetProvince: ['', Validators.required],
    claimetCity: ['', [Validators.required]],
    claimetNeighborhood: [''],
    claimetStreet: ['', [Validators.required, Validators.maxLength(50)]],
    claimetNumber: ['', [Validators.required, Validators.maxLength(5)]],
    claimetPostalCode: [
      '',
      [
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(4)
      ],
    ],
    claimetFloorApartament: ['', [Validators.maxLength(10)]],
    claimetTower: ['', [Validators.maxLength(30)]],
    consent: [false, [Validators.requiredTrue]],
    claimetSelectedTypeOfJob: ['', [Validators.required]],
    claimetWorkday: [
      '',
      [
        Validators.required,
        Validators.maxLength(80),
        Validators.pattern(/^[a-zA-Z0-9\s-]*$/),
      ],
    ],
    claimetGrossRemuneration: [
      null,
      [Validators.required, Validators.maxLength(10)],
    ],
    claimetTypeRemuneration: ['', [Validators.required]],
    claimetDateAdmission: ['', [Validators.required]],
    claimetEgressDate: [],
    claimetCurrentEmploymentStatus: ['', [Validators.required]],
    claimetReasonForDisengagement: [''],
    claimetCauseDisengagement: [''],
    claimetFileDni: [, [this.CountValidatorDni]],
    claimetFilePaycheck: [, [this.CountValidatorPaycheck]],
    department_id: ['', [Validators.required]],
    type_of_representation: [''],
  });

  constructor(
    private formBuilder: FormBuilder,
    private getProvince: GetProvinceUseCase,
    private getLocations: GetLocationsUseCase,
    private getFileUseCase: GetFileUseCase,
    private calendarTranslationService: CalendarTranslationService,
    private messageService: MessageService,
    private getDepartmentsUsecase: GetDepartmentsUseCase,
    private getDepartmentByConciliador: GetDepartmentsByConciliadorUseCase,
    private getDepartmentsByIdUseCase: GetDepartmentsByIdUseCase
  ) {
    this.maxDate = new Date();
    this.maxDateIngreso = new Date();
    this.maxDateIngreso.setMonth(this.maxDateIngreso.getMonth() - 1);
  }

  async ngOnInit(): Promise<void> {
    await this.initializeComponent();
    this.loadUserData();
    this.setupValueChangeHandlers();
  }

  formReady = false;
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['userData'] && changes['userData'].currentValue) {
      this.isRepresentative = this.userData?.rol == 'Representante gremial';
      if (this.isRepresentative) this.addControlers();
      this.loadUserData();
    }
    if (changes['claim'] && changes['claim'].currentValue) {
      this.isEditMode = true;
      this.setupValueChangeHandlers();
      await this.initializeFormWithClaimData();
    }
  }

  async initializeComponent(): Promise<void> {
    await this.loadProvinces();
    this.calendarTranslationService.configureCalendarTranslations();
    this.loadDepartments();
  }

  private addControlers(): void {
    const controls: { [key: string]: any } = {
      type_of_representation: ['', [Validators.required]],
      representativeLastName: ['', [Validators.required]],
      representativeName: ['', [Validators.required]],
      representativeCuil: ['', [Validators.required]],
      representativeEmail: ['', , [Validators.required, Validators.email]],
      representativePhone: [''],
      representativeStreet: ['', , [Validators.required]],
      representativeNumber: ['', , [Validators.required]],
      representativeFloorApartment: [''],
      representativeTower: [''],
      representativePostalCode: [
        '',
        ,
        [Validators.required, Validators.pattern(/^[0-9]*$/)],
      ],
      representativeProvince: ['', [Validators.required]],
      representativeCity: ['', [Validators.required]],
      claimantTypePerformance: ['', Validators.required],
    };

    for (const [key, config] of Object.entries(controls)) {
      const control = Array.isArray(config)
        ? this.formBuilder.control(config[0], config[1])
        : this.formBuilder.control(config);
      this.formClaimetDetails.addControl(key, control);
    }
  }

  private loadUserData() {
    if (this.isEditMode) return;
    if (this.userData) {
      if (this.userData.rol == 'Trabajador') {
        this.formClaimetDetails.patchValue({
          claimetLastname: this.userData.lastName,
          claimetName: this.userData.name,
          claimetCuil: this.userData.cuil,
          claimetEmail: this.userData.email,
          claimetPhone: this.userData.phone,
          claimetProvince: this.userData.province,
          claimetCity: this.userData.city,
          claimetNeighborhood:
            this.userData.neighborhood || this.claim?.claimetNeighborhood,
          claimetStreet: this.userData.street,
          claimetNumber: this.userData.number.toString(),
          claimetPostalCode: this.userData.postalCode,
          claimetFloorApartament: this.userData.floorApartment,
          claimetTower: this.userData.tower,
        });
        this.formReady = true;
      } else {
        this.formClaimetDetails.patchValue({
          representativeLastName: this.userData.lastName,
          representativeName: this.userData.name,
          representativeCuil: this.userData.cuil,
          representativeEmail: this.userData.email,
          representativePhone: this.userData.phone,
          representativeProvince: this.userData.province,
          representativeCity: this.userData.city,
          representativeStreet: this.userData.street,
          representativeNumber: this.userData.number.toString(),
          representativePostalCode: this.userData.postalCode,
          representativeFloorApartment: this.userData.floorApartment,
          representativeTower: this.userData.tower,
          claimantTypePerformance: this.userData.type_responsibility,
        });
        this.formReady = true;
      }
    }
  }

  private handleEmploymentStatusChanges(): void {
    this.formClaimetDetails
      .get('claimetCurrentEmploymentStatus')
      ?.valueChanges.subscribe((selectedValue) => {
        this.showConditionalReason = selectedValue === 'Disuelta';
        this.showConditionalCause = false;

        if (selectedValue !== 'Disuelta') {
          this.formClaimetDetails.patchValue({
            claimetReasonForDisengagement: '',
            claimetCauseDisengagement: '',
          });
        }

        const date = this.formClaimetDetails.get('claimetEgressDate');
        if (selectedValue === 'Disuelta') {
          this.toggleDateValidator(true);
          date?.setValidators([Validators.required]);
        } else {
          date?.clearValidators();
        }
        date?.updateValueAndValidity();
      });
  }

  private setupValueChangeHandlers(): void {
    this.handleEmploymentStatusChanges();
    this.handleDisengagementReasonChanges();
    this.handleProvinceChanges();
    this.handleRepresentationChanges();
    this.handleDateAdmissionChanges();
    this.handleEgressDateChanges();
  }

  private handleDisengagementReasonChanges(): void {
    this.formClaimetDetails
      .get('claimetReasonForDisengagement')
      ?.valueChanges.subscribe((selectedValue) => {
        this.showConditionalCause = selectedValue === 'Despido';
      });
  }

  private handleProvinceChanges(): void {
    this.formClaimetDetails
      .get('claimetProvince')
      ?.valueChanges.subscribe((selectedProvince) => {
        this.loadLocalities(selectedProvince);
        this.formClaimetDetails.patchValue(
          { provincia: selectedProvince },
          { emitEvent: false }
        );
      });
    this.formClaimetDetails
      .get('representativeProvince')
      ?.valueChanges.subscribe((selectedProvince) => {
        this.loadLocalities(selectedProvince);
        this.formClaimetDetails.patchValue(
          { provincia: selectedProvince },
          { emitEvent: false }
        );
      });
  }

  private handleRepresentationChanges(): void {
    this.formClaimetDetails
      .get('type_of_representation')
      ?.valueChanges.subscribe((value) => {
        this.applyRepresentationValidation(value);
      });

    if (this.isEditMode && this.isRepresentative) {
      const currentValue = this.formClaimetDetails.get(
        'type_of_representation'
      )?.value;
      if (currentValue) {
        this.applyRepresentationValidation(currentValue);
      }
    }
  }

  private applyRepresentationValidation(value: string): void {
    this.isRepresentationWorker = value === 'En representación del trabajador';

    const controlsConfig: { [key: string]: ValidatorFn[] } = {
      claimetLastname: [Validators.required],
      claimetName: [Validators.required],
      claimetCuil: [Validators.required, cuilElevenDigitsValidator()],
      claimetEmail: [Validators.required, Validators.email],
      claimetPhone: [],
      claimetProvince: [Validators.required],
      claimetCity: [Validators.required],
      claimetStreet: [Validators.required],
      claimetNumber: [Validators.required],
      claimetPostalCode: [Validators.required],
    };

    Object.entries(controlsConfig).forEach(([controlName, validators]) => {
      const control = this.formClaimetDetails.get(controlName);
      if (!control) return;
      if (this.isRepresentationWorker) {
        control.setValidators(validators);
      } else {
        control.clearValidators();
        control.reset();
      }
      control.updateValueAndValidity();
    });
  }

  private handleDateAdmissionChanges(): void {
    this.formClaimetDetails
      .get('claimetDateAdmission')
      ?.valueChanges.subscribe((date) => {
        if (!this.updatingDateAdmission) {
          this.updatingEgressDate = true;
          this.minDateForEgressDate = date;
          this.formClaimetDetails
            .get('claimetEgressDate')
            ?.updateValueAndValidity();
          this.updatingEgressDate = false;
        }
      });
  }

  private handleEgressDateChanges(): void {
    this.formClaimetDetails
      .get('claimetEgressDate')
      ?.valueChanges.subscribe((date) => {
        if (!this.updatingEgressDate) {
          this.updatingDateAdmission = true;
          this.maxDateForDateAdmission = date;
          this.formClaimetDetails
            .get('claimetDateAdmission')
            ?.updateValueAndValidity();
          this.updatingDateAdmission = false;
        }
      });
  }

  async loadDepartments() {
    const departments = await this.getDepartmentsUsecase.execute();
    if (Array.isArray(departments)) {
      this.departments = this.putIdToDepartmentsList(departments);
    }
  }

  onConsentChange(event: any): void {
    this.formClaimetDetails.controls['consent'].setValue(event.checked);
  }

  stripClaimetCuilDigits(): void {
    if (!this.isRepresentationWorker) return;
    const control = this.formClaimetDetails.get('claimetCuil');
    if (!control) return;
    const digits = String(control.value ?? '')
      .replace(/\D/g, '')
      .slice(0, 11);
    const next = digits.length ? digits : '';
    if (control.value !== next) {
      control.setValue(next, { emitEvent: true });
    } else {
      control.updateValueAndValidity();
    }
  }

  putIdToDepartmentsList(list: Departament[]): Departament[] {
    let listWithId: Departament[] = [];
    for (let i = 0; i < list.length; i++) {
      let department: Departament = {
        id: list[i].id,
        name: list[i].name,
      };
      listWithId.push(department);
    }
    return listWithId;
  }

  yearValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      if (value && (value.toString().length !== 4 || isNaN(value))) {
        return { invalidYear: true };
      }
      return null;
    };
  }

  private toggleDateValidator(enable: boolean): void {
    if (enable) {
      this.formClaimetDetails.setValidators(
        dateControl('claimetDateAdmission', 'claimetEgressDate')
      );
    } else {
      this.formClaimetDetails.clearValidators();
    }
    this.formClaimetDetails.updateValueAndValidity();
  }

  onKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    if (
      input.value.length >= 4 &&
      input.selectionStart === input.selectionEnd
    ) {
      event.preventDefault();
    }

    if (isNaN(Number(event.key))) {
      event.preventDefault();
    }
  }

  private async initializeFormWithClaimData(): Promise<void> {
    if (!this.claim) return;
    if (this.isEditMode) {
      this.selectedDepartment = await this.laodDepartmentsByConciliador2(
        this.claim.department_id
      );
      this.formReady = true;
      this.formClaimetDetails.patchValue({
        claimetLastname: this.claim.claimetLastname || '',
        claimetName: this.claim.claimetName || '',
        claimetCuil: this.claim.claimetCuil || '',
        claimetEmail: this.claim.claimetEmail || '',
        claimetPhone: this.claim.claimetPhone || '',
        claimetCity: this.claim.claimetCity || '',
        claimetNeighborhood: this.claim.claimetNeighborhood || '',
        claimetStreet: this.claim.claimetStreet || '',
        claimetNumber: this.claim.claimetNumber || '',
        claimetPostalCode: this.claim.claimetPostalCode || '',
        claimetFloorApartament: this.claim.claimetFloorApartament || '',
        claimetTower: this.claim.claimetTower || '',
        consent: this.claim.consent || false,
        claimetSelectedTypeOfJob: this.claim.claimetSelectedTypeOfJob || '',
        claimetWorkday: this.claim.claimetWorkday || '',
        claimetGrossRemuneration: this.claim.claimetGrossRemuneration || '',
        claimetTypeRemuneration: this.claim.claimetTypeRemuneration || '',
        claimetDateAdmission: this.claim.claimetDateAdmission || '',
        claimetEgressDate: this.claim.claimetEgressDate || '',
        claimetCurrentEmploymentStatus:
          this.claim.claimetCurrentEmploymentStatus || '',
        claimetReasonForDisengagement:
          this.claim.claimetReasonForDisengagement || '',
        claimetCauseDisengagement: this.claim.claimetCauseDisengagement || '',
        claimetFileDni: this.claim.claimetFileDni || '',
        claimetFilePaycheck: this.claim.claimetFilePaycheck || '',
        department_id: this.claim.department_id || '',
      });
      if (this.isRepresentative) {
        this.formClaimetDetails.patchValue({
          type_of_representation: this.claim?.type_of_representation || '',
          representativeLastName: this.claim?.representativeLastName || '',
          representativeName: this.claim?.representativeName || '',
          representativeCuil: this.claim?.representativeCuil || '',
          representativeEmail: this.claim?.representativeEmail || '',
          representativePhone: this.claim?.representativePhone || '',
          representativeStreet: this.claim?.representativeStreet || '',
          representativeNumber: this.claim?.representativeNumber || '',
          representativeFloorApartment:
            this.claim?.representativeFloorApartment || '',
          representativeTower: this.claim?.representativeTower || '',
          representativePostalCode: this.claim?.representativePostalCode || '',
          representativeProvince: this.claim?.representativeProvince || '',
          representativeCity: this.claim?.representativeCity || '',
          claimantTypePerformance: this.claim?.claimetTypePerformance || '',
        });
        if (this.claim?.type_of_representation) {
          this.applyRepresentationValidation(this.claim.type_of_representation);
        }
      }
      this.formClaimetDetails
        .get('claimetDateAdmission')
        ?.setValue(
          this.parseDate(
            this.formClaimetDetails.get('claimetDateAdmission')?.value
          )
        );
      this.formClaimetDetails
        .get('claimetEgressDate')
        ?.setValue(
          this.parseDate(
            this.formClaimetDetails.get('claimetEgressDate')?.value
          )
        );

      await this.initializeFormWithSavedFiles();
    }
    this.formClaimetDetails.patchValue({
      claimetProvince: this.claim.claimetProvince || '',
    });
  }

  async getFileDni(param: GetFileModel): Promise<string> {
    let result = await this.getFileUseCase.execute(param);
    if (result instanceof Failure) {
      return result.message;
    }
    return result;
  }

  async laodDepartmentsByConciliador(id: string) {
    const departments = await this.getDepartmentByConciliador.execute(id);
    if (Array.isArray(departments)) {
      const result = departments.map((x) => x.id);
      return result;
    }
    return [];
  }

  async laodDepartmentsByConciliador2(
    id: string
  ): Promise<Departament | undefined> {
    const result = await this.getDepartmentsByIdUseCase.execute(id);

    if (!(result instanceof Failure)) {
      return result;
    }
    return undefined;
  }

  onDragOver(event: Event) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDropSuccess(event: any, file: string): any {
    const files = event.dataTransfer?.files;

    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (
        ![
          'image/jpeg',
          'image/png',
          'image/webp',
          'image/heif',
          'application/pdf',
        ].includes(file.type)
      ) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Solo se permiten archivos JPG, WEBP, HEIF, PDF O PNG.',
        });
        return false;
      }
    }

    event.preventDefault();
    this.isDragging = false;
    if (file === 'dni') {
      this.onFileChangeDni(files);
      const fileDniBase64Array = this.imageFiles.map((image) => image.base64);
      this.formClaimetDetails
        .get('claimetFileDni')
        ?.setValue(fileDniBase64Array);
      this.formClaimetDetails.get('claimetFileDni')?.markAsTouched();
      this.formClaimetDetails.get('claimetFileDni')?.updateValueAndValidity();
    } else {
      this.onFileChange(files);
      const filePaycheckBase64Array = this.pdfFiles.map((file) => file.base64);
      this.formClaimetDetails
        .get('claimetFilePaycheck')
        ?.setValue(filePaycheckBase64Array);
      this.formClaimetDetails.get('claimetFilePaycheck')?.markAsTouched();
      this.formClaimetDetails
        .get('claimetFilePaycheck')
        ?.updateValueAndValidity();
    }
  }

  onChange(event: any, file: string): any {
    const input = event.target as HTMLInputElement;
    const files = Array.from(event.target.files) as File[];
    const fileForm = file;

    if (files.length === 0) return;

    // Verifica el límite total de archivos según el tipo
    const currentList = file === 'dni' ? this.imageFiles : this.pdfFiles;
    const maxFiles = file === 'dni' ? 2 : 12;

    if (currentList.length + files.length > maxFiles) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Solo puedes subir hasta ${maxFiles} archivos (JPG, WEBP, HEIF o PDF).`,
      });
      input.value = '';
      return;
    }

    const validTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/heif',
      'application/pdf',
    ];

    const validFiles: File[] = [];

    for (const fileItem of files) {
      if (fileItem.size > 5 * 1024 * 1024) {
        this.messageService.add({
          severity: 'error',
          summary: 'Archivo demasiado grande',
          detail: `El archivo "${fileItem.name}" excede el límite de 5 MB.`,
        });
        continue;
      }

      if (!validTypes.includes(fileItem.type)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error de formato',
          detail: `El archivo "${fileItem.name}" no es un formato permitido.`,
        });
        continue;
      }

      validFiles.push(fileItem);
    }

    for (const fileItem of validFiles) {
      this.convertToBase64(fileItem, fileForm);
    }

    input.value = '';
  }

  private onFileChangeDni(files: any) {
    this.progress = 0;
    const newFiles = Array.from(files);
    this.imageFiles = (this.imageFiles || []).concat(newFiles).slice(-2);
  }
  private onFileChange(files: any) {
    this.progress = 0;
    const newFiles = Array.from(files);
    this.pdfFiles = (this.pdfFiles || []).concat(newFiles);
  }

  private CountValidatorPaycheck(
    control: AbstractControl
  ): { [key: string]: any } | null {
    return control.value?.length >= 1 && control.value?.length <= 12
      ? null
      : { fileCount: true };
  }

  private CountValidatorDni(
    control: AbstractControl
  ): { [key: string]: any } | null {
    return control.value?.length >= 1 && control.value?.length <= 2
      ? null
      : { fileCountDni: true };
  }

  private assignFiles(file: any, base64String: string) {
    if (this.imageFiles.length >= 2) return;

    this.imageFiles.push({ name: file.name, base64: base64String });

    const fileDniBase64Array = this.imageFiles.map((file) => file.base64);
    this.formClaimetDetails.get('claimetFileDni')?.setValue(fileDniBase64Array);
    this.formClaimetDetails.get('claimetFileDni')?.markAsTouched();
    this.formClaimetDetails.get('claimetFileDni')?.updateValueAndValidity();
  }

  private assignFilesReceipt(file: any, base64String: string) {
    if (this.pdfFiles.length >= 12) return;

    this.pdfFiles.push({ name: file.name, base64: base64String });

    const filePaycheckBase64Array = this.pdfFiles.map((file) => file.base64);
    this.formClaimetDetails
      .get('claimetFilePaycheck')
      ?.setValue(filePaycheckBase64Array);
    this.formClaimetDetails.get('claimetFilePaycheck')?.markAsTouched();
    this.formClaimetDetails
      .get('claimetFilePaycheck')
      ?.updateValueAndValidity();
  }

  private async convertToBase64(file: any, fileForm: string) {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result?.toString();
      if (base64String) {
        if (fileForm === 'dni') {
          this.assignFiles(file, base64String);
        } else {
          this.assignFilesReceipt(file, base64String);
        }
      }
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
    };
    reader.readAsDataURL(file);
  }

  deleteFileImage(index: number) {
    this.imageFiles.splice(index, 1);
    const fileDniBase64Array = this.imageFiles.map((image) => image.base64);
    this.formClaimetDetails
      .get('claimetFileDni')
      ?.setValue(fileDniBase64Array.length ? fileDniBase64Array : null);
    this.formClaimetDetails.get('claimetFileDni')?.markAsTouched();
    this.formClaimetDetails.get('claimetFileDni')?.markAsDirty();
    this.checkFileImageCount();
  }

  deleteFilePfd(index: number) {
    this.pdfFiles.splice(index, 1);
    const fileReceiptBase64Array = this.pdfFiles.map((file) => file.base64);
    this.formClaimetDetails
      .get('claimetFilePaycheck')
      ?.setValue(fileReceiptBase64Array.length ? fileReceiptBase64Array : null);
    this.formClaimetDetails.get('claimetFilePaycheck')?.markAsTouched();
    this.formClaimetDetails.get('claimetFilePaycheck')?.markAsDirty();
    this.formClaimetDetails
      .get('claimetFilePaycheck')
      ?.updateValueAndValidity();
  }

  private checkFileImageCount() {
    this.disableFileImageUpload = this.imageFiles.length >= 2;
  }

  hasErrors(controlName: string, errorType: string) {
    return (
      this.formClaimetDetails.get(controlName)?.hasError(errorType) &&
      this.formClaimetDetails.get(controlName)?.touched
    );
  }

  nextStep() {
    if (this.formClaimetDetails.invalid) return;
    this.claimantDetailsForm.emit(this.formClaimetDetails.value);
    this.stepIndex.emit(1);
  }

  cancel() {
    this.claimantDetailsForm.emit(this.formClaimetDetails.value);
    this.activeModal.emit(true);
  }

  onYearSelect(event: any): void {
    const selectedDate = new Date(event);
    const selectedYear = selectedDate.getFullYear().toString();
    this.formClaimetDetails.get('claimetYear')?.setValue(selectedYear);
  }

  async loadProvinces() {
    try {
      const result = await this.getProvince.execute();
      if (Array.isArray(result)) {
        this.provinces = result;
      }
    } catch (error) {
      console.error('Error fetching provinces', error);
    }
  }

  async loadLocalities(provinceId: string) {
    try {
      const localities = await this.getLocations.execute(provinceId);
      if (Array.isArray(localities)) {
        this.localities = this.putIdToLocalitiesList(localities);
      }
    } catch (error) {
      console.error('Error fetching localities', error);
    }
  }

  putIdToLocalitiesList(list: LocationEntity[]): Localities[] {
    let listWithId: Localities[] = [];
    for (let i = 0; i < list.length; i++) {
      let city: Localities = {
        id: i,
        name: list[i].name,
      };
      listWithId.push(city);
    }
    return listWithId;
  }

  minDateValidator(minDate: Date) {
    return (control: FormControl) => {
      const controlDate = new Date(control.value);
      if (control.value && controlDate < minDate) {
        return { minDate: true };
      }
      return null;
    };
  }

  private async initializeFormWithSavedFiles(): Promise<void> {
    try {
      const dniValues =
        this.formClaimetDetails.get('claimetFileDni')?.value || [];
      if (!this.claim?.claimetFileDni || dniValues.length === 0) {
        console.warn('No hay archivos DNI para procesar.');
        return;
      }

      const dniFilesPromises = dniValues.map((_: any, i: number) => {
        const dniFile = new GetFileModel(
          this.claim?.id!,
          this.claim?.claimetFileDni[i]!
        );
        return this.getFile(dniFile)
          .then((bas64) => {
            if (bas64) {
              return {
                name: dniFile.fileName,
                base64: 'data:image/png;base64,' + bas64,
              };
            } else {
              console.error('Archivo DNI vacío o inválido.');
              return null;
            }
          })
          .catch((error) => {
            console.error('Error al obtener archivo DNI:', error);
            return null;
          });
      });

      const resolvedDniFiles = (await Promise.all(dniFilesPromises)).filter(
        (file) => file !== null
      );
      this.imageFiles = resolvedDniFiles;

      if (this.claim?.claimetFilePaycheck?.length) {
        try {
          this.pdfFiles = [];

          let i = 0;
          for (const fileName of this.claim.claimetFilePaycheck) {
            const paycheckFile = new GetFileModel(this.claim.id!, fileName);
            const base64 = await this.getFile(paycheckFile);

            if (base64) {
              let base64Objet: Base64 = {
                name: 'Recibo de sueldo ' + (i + 1),
                base64: 'data:application/pdf;base64,' + base64,
              };

              this.pdfFiles.push(base64Objet);
              i++;
            } else {
              console.error(`Archivo ${fileName} vacío o inválido.`);
            }
          }

          if (this.pdfFiles.length === 0) {
            console.warn('Ningún archivo válido fue procesado.');
          }
        } catch (error) {
          console.error(
            'Error al obtener archivos de recibo de sueldo:',
            error
          );
          this.pdfFiles = [];
        }
      } else {
        console.warn(
          'No se encontraron archivos de recibo de sueldo para procesar.'
        );
      }

      this.formClaimetDetails.get('claimetFileDni')?.setValue(this.imageFiles);
      this.formClaimetDetails
        .get('claimetFilePaycheck')
        ?.setValue(this.pdfFiles);
    } catch (error) {
      console.error(
        'Error general al inicializar archivos en el formulario:',
        error
      );
    }
  }

  async getFile(param: GetFileModel): Promise<string> {
    let result = await this.getFileUseCase.execute(param);
    if (result instanceof Failure) {
      return result.message;
    }
    return result;
  }

  parseDate(date: string): Date {
    if (!date) {
      return new Date(1930, 0, 1);
    }
    let part = date.split(/\/| |:/);
    return new Date(
      parseInt(part[2]),
      parseInt(part[0]) - 1,
      parseInt(part[1]),
      parseInt(part[3]),
      parseInt(part[4]),
      parseInt(part[5])
    );
  }

  showError() {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Message Content',
    });
  }
}
